import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Popover, Stack, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/**
 * "Ready to publish" / "3 of 5 ready" pill with a checklist popover and one-click fixes.
 * Bumping `openSignal` opens the popover from elsewhere on the page.
 */
export default function AppReadinessButton({
  readiness,
  onFix,
  canEdit,
  openSignal = 0,
  readyLabel = 'Ready to publish',
  readyTitle = 'Everything needed is in place',
  pendingTitle = 'Before you publish',
  subtitle = 'These checks keep the public app from showing empty or broken panels.',
  testId = 'app-readiness',
}) {
  const buttonRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const {
    ready, done, total, items,
  } = readiness;
  const pct = total ? Math.round((done / total) * 100) : 0;

  useEffect(() => {
    if (openSignal && buttonRef.current) setAnchor(buttonRef.current);
  }, [openSignal]);

  return (
    <>
      <Button
        ref={buttonRef}
        size="small"
        variant="outlined"
        color={ready ? 'success' : 'warning'}
        onClick={(e) => setAnchor(e.currentTarget)}
        data-testid={testId}
        startIcon={ready ? <CheckCircleIcon /> : (
          <Box sx={{
            position: 'relative', display: 'inline-flex', width: 18, height: 18,
          }}
          >
            <CircularProgress variant="determinate" value={100} size={18} thickness={5} sx={{ color: 'action.disabledBackground', position: 'absolute' }} />
            <CircularProgress variant="determinate" value={pct} size={18} thickness={5} color="warning" />
          </Box>
        )}
        sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 600 }}
      >
        {ready ? readyLabel : `${done} of ${total} ready`}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, p: 2, borderRadius: 3 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          {ready ? readyTitle : pendingTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {subtitle}
        </Typography>
        <Stack spacing={1}>
          {items.map((item) => (
            <Stack key={item.id} direction="row" spacing={1} alignItems="flex-start">
              {item.ok
                ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.25 }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: item.optional ? 'text.disabled' : 'warning.main', mt: 0.25 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color={item.ok ? 'text.secondary' : 'text.primary'}>
                  {item.label}
                  {item.optional && !item.ok ? ' (optional)' : ''}
                </Typography>
                {!item.ok && item.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>{item.detail}</Typography>
                )}
                {!item.ok && item.extra?.map((line) => (
                  <Typography key={line} variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>{line}</Typography>
                ))}
              </Box>
              {!item.ok && item.fix && canEdit && (
                <Button
                  size="small"
                  onClick={() => {
                    setAnchor(null);
                    onFix(item);
                  }}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0 }}
                >
                  {item.fixLabel}
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
}
